import type {
  ActivityDefinition,
  CarePlan,
  PlanDefinition,
} from '@medplum/fhirtypes';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  CarePlanInstancePreview,
  MarkdownProse,
  PlanSpecView,
  fetchPlanActivityDefinitions,
  fetchPlanDefinitionForCarePlan,
  loadCarePlanInstance,
} from '@eluve/care-plan-render';
import { Heading, Stack, Tag, Text } from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import { useTranslation } from '@eluve/i18n';
import { useMedplum, useMedplumPatientAccess } from '@eluve/medplum-client';
import { type PhasedInstanceSpec, fhirToSpec } from '@eluve/plan-shapes';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';

import {
  carePlanActivitySummaries,
  carePlanPlanTitle,
  planConditions,
  planDefinitionTitle,
} from './carePlanFhir';

const statusColor = (status: string | undefined) =>
  status === 'active' || status === 'in-progress' ? 'lightgreen' : 'gray';

/**
 * Deep-linkable detail view for a single CarePlan. Reads `carePlanId` straight
 * from the route and fetches the resource by id, so a cold link / refresh
 * works without any list state. The plan is only rendered when its subject
 * matches the route's patient (resolved via `useMedplumPatientAccess`), so a
 * care plan id can't be viewed under another patient's profile. Resolves the
 * instantiated PlanDefinition (when present) for the protocol title,
 * description, and conditions.
 *
 * The plan body renders the same structured views as the encounter, read-only:
 * a phased instance decodes (+ its Goals/ServiceRequests) into a spec shown
 * through `CarePlanInstancePreview` (spec passed as both seed and spec so no
 * tailoring-diff chrome shows); a verbatim continuous/episodic plan has no
 * decodable instance, so its body renders from the template via `PlanSpecView`
 * — the same shape view (control bands, baseline + events) the appointment
 * shows. CarePlans with no instantiated template fall back to a flat activity
 * list.
 */
export const PatientCarePlanDetail: React.FC = () => {
  const { t } = useTranslation();
  const { patientId, carePlanId } = useParams() as {
    patientId: string;
    carePlanId: string;
  };
  const isCarePlansEnabled = useIsPostHogFeatureFlagEnabled('CARE_PLANS');
  const { client } = useMedplum();
  const {
    isLoading: isAccessLoading,
    hasAccess,
    fhirPatientId,
  } = useMedplumPatientAccess(patientId);

  const [carePlan, setCarePlan] = useState<CarePlan | null>(null);
  const [planDefinition, setPlanDefinition] = useState<PlanDefinition | null>(
    null,
  );
  const [instanceSpec, setInstanceSpec] = useState<PhasedInstanceSpec | null>(
    null,
  );
  const [activityDefinitions, setActivityDefinitions] = useState<
    ActivityDefinition[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!client || !carePlanId || !fhirPatientId) {
      setIsLoading(false);
      setCarePlan(null);
      setPlanDefinition(null);
      setInstanceSpec(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    setPlanDefinition(null);
    setInstanceSpec(null);
    setActivityDefinitions([]);

    (async () => {
      try {
        const cp = (await client.readResource(
          'CarePlan',
          carePlanId,
        )) as CarePlan;
        if (cancelled) return;
        if (cp.subject?.reference !== `Patient/${fhirPatientId}`) {
          setCarePlan(null);
          setIsError(true);
          return;
        }
        setCarePlan(cp);

        // The protocol (PlanDefinition) and the decoded instance are
        // independent reads; fetch them together.
        const [pd, instance] = await Promise.all([
          fetchPlanDefinitionForCarePlan(client, cp),
          loadCarePlanInstance(client, cp),
        ]);
        // A verbatim (continuous/episodic) plan has no decodable instance, so
        // its body renders from the template — fetch the template's
        // ActivityDefinitions so activity entries recover their detail.
        const ads = pd ? await fetchPlanActivityDefinitions(client, pd) : [];
        if (!cancelled) {
          setPlanDefinition(pd ?? null);
          setInstanceSpec(instance.seed);
          setActivityDefinitions(ads);
        }
      } catch {
        if (!cancelled) {
          setCarePlan(null);
          setIsError(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client, carePlanId, fhirPatientId]);

  // A verbatim (non-phased) plan renders its template's shape directly — the
  // same structured view the encounter shows — when there's no decodable
  // phased instance to diff against.
  const verbatimSpec = useMemo(
    () =>
      planDefinition
        ? fhirToSpec(planDefinition, { activityDefinitions })
        : null,
    [planDefinition, activityDefinitions],
  );

  if (!isCarePlansEnabled) return null;

  const backLink = (
    <Link
      to="../care-plans"
      className="text-sm text-text-accent-secondary hover:underline"
    >
      {t('patients.carePlans.backToList')}
    </Link>
  );

  if (isAccessLoading) {
    return (
      <Stack gap={3} className="pt-2">
        {backLink}
        <Text color="tertiary">{t('patients.common.checkingAccess')}</Text>
      </Stack>
    );
  }

  if (!hasAccess) {
    return (
      <Stack gap={3} className="pt-2">
        {backLink}
        <Text color="tertiary">{t('patients.carePlans.noFhirForDetail')}</Text>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack gap={3} className="pt-2">
        {backLink}
        <Text color="tertiary">{t('patients.carePlans.loadingOne')}</Text>
      </Stack>
    );
  }

  if (isError || !carePlan) {
    return (
      <Stack gap={3} className="pt-2">
        {backLink}
        <Text color="tertiary">{t('patients.carePlans.detailLoadError')}</Text>
      </Stack>
    );
  }

  const title = planDefinition
    ? planDefinitionTitle(planDefinition)
    : carePlanPlanTitle(carePlan);
  const status = carePlan.status ?? 'unknown';
  const start = carePlan.period?.start;
  const end = carePlan.period?.end;
  const conditions = planDefinition ? planConditions(planDefinition) : [];

  return (
    <Stack gap={6} className="pt-2">
      {backLink}

      <Stack gap={2}>
        <div className="flex items-center gap-3">
          <Heading size="lg" weight="semibold">
            {title}
          </Heading>
          <Tag label={status} color={statusColor(status)} />
        </div>
        {(start || end) && (
          <Text size="sm" color="tertiary">
            {start
              ? formatDate.toDisplay(start)
              : t('patients.carePlans.unknownStart')}
            {' – '}
            {end ? formatDate.toDisplay(end) : t('patients.carePlans.ongoing')}
          </Text>
        )}
      </Stack>

      {planDefinition?.description && (
        <section>
          <Text weight="semibold" className="mb-1">
            {t('patients.carePlans.protocol')}
          </Text>
          <MarkdownProse
            content={planDefinition.description}
            className="text-sm text-text-secondary"
          />
        </section>
      )}

      {conditions.length > 0 && (
        <section>
          <Text weight="semibold" className="mb-2">
            {t('patients.carePlans.conditions')}
          </Text>
          <div className="flex flex-wrap gap-2">
            {conditions.map((condition) => (
              <Tag key={condition} label={condition} color="lightblue" />
            ))}
          </div>
        </section>
      )}

      {instanceSpec ? (
        <CarePlanInstancePreview
          seed={instanceSpec}
          spec={instanceSpec}
          showHeader={false}
        />
      ) : verbatimSpec ? (
        <PlanSpecView spec={verbatimSpec} />
      ) : (
        <FlatActivityList carePlan={carePlan} />
      )}
    </Stack>
  );
};

/**
 * Fallback for CarePlans that don't decode into a structured phased instance
 * (no `instantiatesCanonical`, or not produced by the care-plan pipeline). The
 * original flat, coding-agnostic activity list off `CarePlan.activity`.
 */
const FlatActivityList: React.FC<{ carePlan: CarePlan }> = ({ carePlan }) => {
  const { t } = useTranslation();
  const activities = carePlanActivitySummaries(carePlan);
  return (
    <section>
      <Text weight="semibold" className="mb-2">
        {t('patients.carePlans.activities')}
      </Text>
      {activities.length === 0 ? (
        <Text size="sm" color="tertiary">
          {t('patients.carePlans.noActivities')}
        </Text>
      ) : (
        <Stack gap={2}>
          {activities.map((activity, index) => (
            <div
              key={`${activity.title}-${index}`}
              className="rounded-lg border p-3"
            >
              <div className="flex items-center justify-between gap-3">
                <Text weight="medium">{activity.title}</Text>
                {activity.status && (
                  <Tag
                    label={activity.status}
                    color={statusColor(activity.status)}
                  />
                )}
              </div>
              {activity.description && (
                <Text size="sm" color="secondary" className="mt-1">
                  {activity.description}
                </Text>
              )}
              {(activity.start || activity.end) && (
                <Text size="xs" color="tertiary" className="mt-1">
                  {activity.start ? formatDate.toDisplay(activity.start) : '—'}
                  {' – '}
                  {activity.end ? formatDate.toDisplay(activity.end) : '—'}
                </Text>
              )}
            </div>
          ))}
        </Stack>
      )}
    </section>
  );
};
